import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { labAvailabilityTemplate } from '../../../apollo/templates/organization/query.graphql';
import LabAvailabilityCalendar from './lab-availability-calender.component';
import SkeletonCard from '../../shared/skeleton/skeleton-card.component';
import { formatMonthKey } from 'utils/format';

// These are the API's month keys, not labels: they go out lowercased as the `months` query
// variable and come back as the `month` field of every result. Localize them for display
// with formatMonthKey, never here. DD-3807.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type LabAvailabilityActionsProps = {
    labId: string;
};

const LabAvailabilityActions: FC<LabAvailabilityActionsProps> = ({ labId }) => {
    const { t: translate } = useTranslation(['common', 'organization', 'users']);
    const [selectedMonths, setSelectedMonths] = useState<string[]>(MONTHS);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [labData, setLabData] = useState<any>(null);
    const [years, setYears] = useState<number[]>([]);

    const { data, loading } = useQuery(labAvailabilityTemplate, {
        variables: {
            labId,
            year: selectedYear.toString(),
            months: selectedMonths.map((month) => month.toLowerCase()),
        },
        skip: selectedMonths.length === 0,
        fetchPolicy: 'cache-and-network',
        onCompleted: (result) => {
            if (result?.labAvailability?.lab) {
                setLabData(result.labAvailability.lab);
            }
            if (result?.labAvailability?.connectYear && years.length === 0) {
                const calculatedYears = Array.from(
                    { length: new Date().getFullYear() - Number(result.labAvailability.connectYear) + 1 },
                    (_, i) => Number(result.labAvailability.connectYear) + i,
                );
                setYears(calculatedYears);
            }
        },
    });

    const handleCheckAll = () => setSelectedMonths(MONTHS);
    const handleUncheckAll = () => setSelectedMonths([]);

    const handleMonthChange = (month: string) => {
        setSelectedMonths((prev) => (prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]));
    };

    return (
        <Box>
            {loading && !labData ? (
                <SkeletonCard hasAvatar={true} hasTitle={true} textLines={0} hasActions={false} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {labData &&
                        (labData.logoSignedUrl ? (
                            <img
                                src={labData.logoSignedUrl}
                                alt={labData.name}
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    marginRight: 16,
                                    boxShadow: '0 0 5px rgba(0,0,0,0.1)',
                                }}
                            />
                        ) : (
                            <Avatar sx={{ width: 80, height: 80, mr: 3, bgcolor: 'grey.500' }}>
                                {labData.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                        ))}
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            {labData?.name}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Typography sx={{ fontWeight: 700 }}>{translate('organization:YEAR')}:</Typography>
                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value as number)}
                        sx={{ borderRadius: 1.5 }}
                    >
                        {years.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 700 }}>{translate('organization:MONTHS')}:</Typography>
                    <Select
                        multiple
                        value={selectedMonths}
                        displayEmpty
                        sx={{ borderRadius: 1.5 }}
                        renderValue={(selected) =>
                            selected.length === 0
                                ? translate('organization:SELECT_MONTHS')
                                : `${selected.length} ${selected.length > 1 ? translate('organization:MONTHS_SELECTED') : translate('organization:MONTH_SELECTED')}`
                        }
                    >
                        <MenuItem sx={{ justifyContent: 'space-between', gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={handleCheckAll} sx={{ minWidth: 80 }}>
                                {translate('organization:SELECT_ALL')}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={handleUncheckAll}
                                sx={{ minWidth: 80 }}
                            >
                                {translate('organization:CLEAR_ALL')}
                            </Button>
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        {MONTHS.map((month) => (
                            <MenuItem
                                key={month}
                                value={month}
                                onClick={() => handleMonthChange(month)}
                                sx={{ py: 0.5 }}
                            >
                                <Checkbox checked={selectedMonths.includes(month)} size="small" />
                                <ListItemText
                                    primary={formatMonthKey(month)}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box>
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    {translate('organization:MONTHLY_AVAILABILITY')}
                </Typography>

                {loading && selectedMonths.length > 0 ? (
                    <Box sx={{ mt: 4 }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            {Array.from({ length: Math.min(selectedMonths.length, 3) }).map((_, index) => (
                                <SkeletonCard key={index} hasTitle={true} textLines={5} width={300} height={200} />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    data?.labAvailability?.monthlyAvailability && (
                        <LabAvailabilityCalendar monthlyAvailability={data?.labAvailability?.monthlyAvailability} />
                    )
                )}
            </Box>
        </Box>
    );
};

export default LabAvailabilityActions;
